<template>
  <Select
    v-if="!multi"
    :id="title"
    v-model="model"
    :disabled="isDisabled"
    :name="name"
    :option-label="labelField"
    :virtual-scroller-options="{ lazy: true, itemSize: 25, showLoader: false, delay: 250 }"
    :options="results"
    class="w-full"
    filter
    option-value="id"
    placeholder="Sélectionner"
    :loading="isLoading"
    :show-clear="!isDisabled"
    fluid
    @update:model-value="selectionChange"
  />

  <MultiSelect
    v-else
    :id="title"
    v-model="model"
    :name="name"
    variant="filled"
    :option-label="labelField"
    :virtual-scroller-options="{ lazy: true, itemSize: 25, showLoader: false, delay: 250 }"
    :options="results"
    placeholder="Sélectionner"
    class="w-full"
    filter
    option-value="id"
    :show-clear="!isDisabled"
    :loading="isLoading"
    :disabled="isDisabled"
    :max-selected-labels="10"
    fluid
    @update:model-value="selectionChange"
  />
</template>

<script setup lang="ts">
import { number } from "zod";

const props = defineProps({
  title: String,
  id: Number,
  name: String,
  apiUrl: String,
  includes: String,
  parentApi: String,
  parentId: Number,
  childRelation: String,
  mode: String,
  paramName: String,
  paramValue: String,
  multi: { type: Boolean, required: false, default: false },
  labelField: { type: String, required: false, default: "name" },
  isDisabled: { type: Boolean, required: false, default: false },
  noPagination: { type: Boolean, required: false, default: true },
});

const isLoading = ref<boolean>(true);
const model = defineModel();
const $apiFetch = useSanctumClient();
const results = ref([]);
// TODO : make URL reactive to remove re-render
const url = computed(() => {
  isLoading.value = true;
  let _url = props.apiUrl;
  if (props.parentApi && props.parentId && props.childRelation) {
    _url = props.parentApi + "/" + props.parentId + "/" + props.childRelation;
  }

  if (_url) {
    const query = new URLSearchParams("");
    if (props.noPagination)
      query.append("no_pagination", "true");
    if (props.includes)
      query.append("include", props.includes);
    if (props.paramName && props.paramValue)
      query.append(props.paramName, props.paramValue);
    _url = _url + "?" + query;
  }
  return _url;
});

// Même garde que searchPlus : seule la dernière requête lancée écrit les résultats,
// et une erreur ne laisse pas l'indicateur de chargement tourner.
let lastRequest = 0;

const fetchData = async () => {
  const request = ++lastRequest;
  isLoading.value = true;
  try {
    if (url.value) {
      const response = await $apiFetch(`${url.value}`);
      if (request === lastRequest) results.value = response.data;
    }
  }
  catch {
    if (request === lastRequest) results.value = [];
  }
  finally {
    if (request === lastRequest) isLoading.value = false;
  }
};

fetchData(); // Don't await - let it load asynchronously
watch(async () => props.parentId, async () => await fetchData());

const emit = defineEmits<{
  selection: [id: number, object: object];
}>();

function selectionChange(id: number, object: object) {
  if (id) {
    const selectedObject = results.value?.find(x => x.id === id);
    emit("selection", id, selectedObject);
  }
  else {
    emit("selection", null, null);
  }
}
</script>

<style scoped>
:deep(.p-multiselect-label) {
  overflow-inline: auto;
}
</style>
